import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import {
  validateCollection,
  validateCollectionField,
  hasError,
  firstError,
} from "../../collections/collectionValidation";
import MediaDialog from "../../common/MediaDialog.js";
import EditColumn from "../../common/EditColumn";
import NewThumbnailOverlay from "../../common/NewThumbnailOverlay";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useAddToModule, useModules } from "../../modules/moduleServices";
// import { useSearch } from "../../searchServices";
import {
  useCollectionCreate,
  useUploadCollectionThumbnail,
} from "../../collections/collectionServices";

import { MediaContext } from "../../common/media-context";
import { isBlank, notBlank } from "../../../util/ext";
import ModuleAutoComplete from "../../common/ModuleAutocomplete";
import { validateModuleField } from "../../modules/moduleValidation";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "0",
  },

  dialog: {
    width: "866px",
    borderRadius: "20px",
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
  },

  close: {
    width: "40px",
    height: "40px",
    position: "relative",
    right: "-10px",
  },

  title: {
    textAlign: "left",
    color: theme.palette.brandPrimary,
  },

  description: {
    padding: theme.spacing(6, 0, 0, 0),
  },

  loadingIndicator: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.palette.secondary.dark,
    opacity: 0.7,
  },
}));

const validator = {
  validateField: validateCollectionField,
  validate: validateCollection,
};

const initialCollection = {
  name: "",
  mediaType: "collection",
  description: "",
  descriptionText: "",
  thumbnailUrl: "",
};

const NewCollectionDialog = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const { open, onSave, onClose } = props;

  const { SnackAlert, showAlert } = useSnackAlert();

  //-- Set up overall state
  const [collection, setCollection] = useState(initialCollection);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [parentModule, setParentModule] = useState({});
  const [isChild, setIsChild] = useState(false);

  useEffect(() => {
    setSaveDisabled(
      loading ||
        isBlank(collection.name) ||
        isBlank(collection.description) ||
        isBlank(thumbnailUrl) ||
        (isChild && isBlank(parentModule)),
    );
  }, [collection, isChild, parentModule, thumbnailUrl, loading]);

  const handleCheckboxChange = event => {
    setIsChild(event.target.checked);
  };

  const results = useModules();
  const [modules, setModules] = useState([]);
  useEffect(() => {
    if (results.isSuccess) {
      setModules(results.data);
    }
  }, [results.data, results.isSuccess]);

  const handleClose = useCallback(() => {
    setCollection(initialCollection);
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setIsChild(false);
    setErrors({});
    setLoading(false);
    onClose();
  }, [onClose, setLoading]);

  const addToModule = useAddToModule(parentModule.id);
  const createCollection = useCollectionCreate();
  const uploadThumbnail = useUploadCollectionThumbnail();
  const handleSave = useCallback(async () => {
    setLoading(true);
    setErrors(validateCollection(collection) || {});

    if (isBlank(errors)) {
      try {
        const newCollection = await createCollection.mutateAsync({
          data: collection,
        });

        await uploadThumbnail.mutateAsync({
          id: newCollection.id,
          thumbnail: thumbnailFile,
        });

        if (isChild && notBlank(parentModule.id)) {
          await addToModule.mutateAsync({
            resourceIds: { id: parentModule.id },
            data: { ids: [newCollection.id] },
          });
        }

        const view = isChild
          ? `module,collection`
          : `ungrouped-all,ungrouped-collections,ungrouped-collection`;
        const id = isChild
          ? `${parentModule.id},${newCollection.id}`
          : newCollection.id;

        setLoading(false);
        onSave({
          redirectPath: `/creator/organize?view=${view}&id=${id}`,
          mediaType: "collection",
        });
        handleClose();
      } catch (error) {
        showAlert("error", [error.message]);
      }
    } else {
      setLoading(false);
      showAlert("error", [
        "There are errors that should be fixed before saving.",
      ]);
    }
  }, [
    collection,
    createCollection,
    uploadThumbnail,
    thumbnailFile,
    handleClose,
    errors,
    showAlert,
    onSave,
    addToModule,
    parentModule,
    isChild,
  ]);

  const formBuilder = useCallback(
    handlers => {
      return (
        <MediaDialog
          maxWidth="xl"
          open={open}
          title="Create Collection"
          handleClose={handleClose}
          loading={loading}
          content={
            <Grid container>
              <Grid item style={{ marginRight: "16px" }}>
                <NewThumbnailOverlay
                  accept="jpg, jpeg, image/jpeg, image/jpg"
                  showAlert={showAlert}
                />
              </Grid>
              <Grid item xs>
                <TextField
                  id="collection-name"
                  fullWidth
                  required
                  label="Title"
                  value={context.media.name}
                  onChange={handlers.onTextChange(["name"])}
                  error={hasError(errors, "name")}
                  helperText={firstError(errors, "name")}
                  onBlur={() => {
                    setErrors(validateCollectionField("name", collection.name));
                  }}
                />
                <EditDescriptionColumn
                  allowHtml
                  enableLinks
                  required
                  className={classes.description}
                  error={hasError(errors, "description")}
                  onBlur={() =>
                    setErrors(
                      validateModuleField(
                        "description",
                        collection.description,
                      ),
                    )
                  }
                />

                <FormControlLabel
                  label="Add to module:"
                  control={
                    <Checkbox
                      color="primary"
                      checked={isChild}
                      onChange={handleCheckboxChange}
                    />
                  }
                />

                <ModuleAutoComplete
                  label={isChild ? "Please select a module" : "Module"}
                  modules={modules}
                  setSelectedModule={setParentModule}
                  selectedModule={parentModule}
                />
              </Grid>
            </Grid>
          }
          actions={
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveDisabled}
            >
              Save
            </Button>
          }
        />
      );
    },
    [
      classes,
      handleClose,
      handleSave,
      isChild,
      modules,
      showAlert,
      context,
      open,
      collection,
      loading,
      errors,
      saveDisabled,
      parentModule,
    ],
  );

  return (
    <MediaContext.Provider
      value={{
        initial: initialCollection,
        media: collection,
        setMedia: setCollection,
        errors: errors,
        setErrors: setErrors,
        thumbnailUrl: thumbnailUrl,
        setThumbnailUrl: setThumbnailUrl,
        thumbnailFile: thumbnailFile,
        setThumbnailFile: setThumbnailFile,
      }}
    >
      <Fragment>
        <SnackAlert />
        <EditColumn
          isNew
          className={classes.root}
          formBuilder={formBuilder}
          validator={validator}
        />
      </Fragment>
    </MediaContext.Provider>
  );
};

export default NewCollectionDialog;
